import { useState, useEffect, useCallback } from 'react'
import { createRoute } from '@tanstack/react-router'
import { Bug, RefreshCw, CheckCircle2, XCircle, Clock, Activity, AlertTriangle } from 'lucide-react'
import { lawyerRoute } from './index'
import api from '@/utils/api/client'

interface SessionStatus {
  state: string
  last_check: string | null
  last_refresh: string | null
  consecutive_failures: number
  circuit_breaker_open: boolean
  circuit_breaker_until: string | null
  error_message: string | null
  session_created_at: string | null
  session_age_seconds: number | null
}

interface SessionStats {
  total_checks: number
  successful_checks: number
  failed_checks: number
  refreshes_triggered: number
  successful_refreshes: number
  failed_refreshes: number
  circuit_opens: number
  uptime_percentage: number
}

interface SessionEvent {
  type: string
  timestamp: number
  iso_time: string
  details: Record<string, unknown>
}

interface DebugResponse {
  current_status: SessionStatus
  stats: SessionStats
  events: SessionEvent[]
  check_interval_seconds: number
}

function formatTimeAgo(isoTime: string | null): string {
  if (!isoTime) return 'Never'
  // Backend returns ISO timestamps without timezone suffix - they are UTC
  // Append 'Z' if not present to ensure proper UTC parsing
  const normalizedTime = isoTime.endsWith('Z') ? isoTime : isoTime + 'Z'
  const date = new Date(normalizedTime)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)

  if (diffSeconds < 60) return `${diffSeconds}s ago`
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ${diffMinutes % 60}m ago`
  return date.toLocaleString()
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return 'Unknown'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  if (mins === 0) return `${secs}s`
  return `${mins}m ${secs}s`
}

function formatEventTime(isoTime: string): string {
  // Backend returns ISO timestamps without timezone suffix - they are UTC
  const normalizedTime = isoTime.endsWith('Z') ? isoTime : isoTime + 'Z'
  return new Date(normalizedTime).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function getEventColor(eventType: string): string {
  if (eventType.includes('success')) return 'bg-green-500'
  if (eventType.includes('failed') || eventType.includes('circuit_open')) return 'bg-red-500'
  if (eventType.includes('refresh_start')) return 'bg-amber-500'
  if (eventType.includes('check_start')) return 'bg-blue-400'
  return 'bg-slate-400'
}

function getEventIcon(eventType: string) {
  if (eventType.includes('success')) return <CheckCircle2 className="h-4 w-4 text-green-600" />
  if (eventType.includes('failed')) return <XCircle className="h-4 w-4 text-red-600" />
  if (eventType.includes('circuit_open')) return <AlertTriangle className="h-4 w-4 text-red-600" />
  if (eventType.includes('circuit_close')) return <CheckCircle2 className="h-4 w-4 text-green-600" />
  if (eventType.includes('refresh')) return <RefreshCw className="h-4 w-4 text-amber-600" />
  return <Clock className="h-4 w-4 text-slate-500" />
}

function getStateColor(state: string): string {
  switch (state) {
    case 'valid': return 'text-green-600'
    case 'refreshing': return 'text-amber-600'
    case 'error': return 'text-red-600'
    default: return 'text-slate-500'
  }
}

function getStateBgColor(state: string): string {
  switch (state) {
    case 'valid': return 'bg-green-100'
    case 'refreshing': return 'bg-amber-100'
    case 'error': return 'bg-red-100'
    default: return 'bg-slate-100'
  }
}

// Timeline visualization component
function Timeline({ events }: { events: SessionEvent[] }) {
  if (events.length === 0) {
    return (
      <div className="text-center text-slate-500 py-8">
        No events recorded in the last 24 hours
      </div>
    )
  }

  // Group events by hour for visualization
  const now = new Date()
  const hours24Ago = new Date(now.getTime() - 24 * 60 * 60 * 1000)

  // Create 24 hourly buckets
  const buckets: { hour: number; events: SessionEvent[] }[] = []
  for (let i = 0; i < 24; i++) {
    const bucketTime = new Date(hours24Ago.getTime() + i * 60 * 60 * 1000)
    buckets.push({
      hour: bucketTime.getHours(),
      events: [],
    })
  }

  // Assign events to buckets
  events.forEach(event => {
    // Backend returns ISO timestamps without timezone suffix - they are UTC
    const normalizedTime = event.iso_time.endsWith('Z') ? event.iso_time : event.iso_time + 'Z'
    const eventTime = new Date(normalizedTime)
    const hoursSince = Math.floor((eventTime.getTime() - hours24Ago.getTime()) / (60 * 60 * 1000))
    if (hoursSince >= 0 && hoursSince < 24) {
      buckets[hoursSince].events.push(event)
    }
  })

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-0.5 h-16">
        {buckets.map((bucket, idx) => {
          const hasSuccess = bucket.events.some(e => e.type.includes('success'))
          const hasFailure = bucket.events.some(e => e.type.includes('failed') || e.type === 'circuit_open')
          const hasRefresh = bucket.events.some(e => e.type === 'refresh_start' || e.type === 'refresh_success')

          let bgColor = 'bg-slate-200'
          if (hasFailure) bgColor = 'bg-red-400'
          else if (hasRefresh) bgColor = 'bg-amber-400'
          else if (hasSuccess) bgColor = 'bg-green-400'
          else if (bucket.events.length > 0) bgColor = 'bg-blue-300'

          const height = bucket.events.length === 0 ? 4 : Math.min(64, 4 + bucket.events.length * 8)

          return (
            <div
              key={idx}
              className={`flex-1 rounded-t ${bgColor} transition-all hover:opacity-80`}
              style={{ height: `${height}px` }}
              title={`${bucket.hour}:00 - ${bucket.events.length} events`}
            />
          )
        })}
      </div>
      <div className="flex justify-between text-xs text-slate-400">
        <span>{hours24Ago.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
        <span>Now</span>
      </div>
    </div>
  )
}

function DebugPage() {
  const [data, setData] = useState<DebugResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(true)

  const fetchDebugData = useCallback(async () => {
    try {
      const response = await api.get('/api/chat/amadeus-debug/')
      setData(response.data)
      setError(null)
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch debug data'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDebugData()
  }, [fetchDebugData])

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(fetchDebugData, 30000)
    return () => clearInterval(interval)
  }, [autoRefresh, fetchDebugData])

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-slate-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4">
        <XCircle className="h-12 w-12 text-red-500" />
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchDebugData}
          className="px-4 py-2 bg-action-500 text-white rounded-lg hover:bg-action-600"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!data) return null

  const { current_status: status, stats, events, check_interval_seconds } = data

  return (
    <div className="h-full flex flex-col p-6 lg:p-8 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bug className="h-8 w-8 text-action-500" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Amadeus Debug</h1>
            <p className="text-slate-500">Session-Monitoring und Event-Historie</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={fetchDebugData}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            title="Refresh now"
          >
            <RefreshCw className="h-5 w-5 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        {/* Current State */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Current State</div>
          <div className={`text-xl font-semibold ${getStateColor(status.state)} flex items-center gap-2`}>
            <span className={`inline-block w-3 h-3 rounded-full ${getStateBgColor(status.state).replace('bg-', 'bg-').replace('100', '500')}`} />
            {status.state.charAt(0).toUpperCase() + status.state.slice(1)}
          </div>
          {status.circuit_breaker_open && (
            <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Circuit breaker open
            </div>
          )}
        </div>

        {/* Session Age */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Session Age</div>
          <div className="text-xl font-semibold text-slate-900">
            {formatDuration(status.session_age_seconds)}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Created: {status.session_created_at ? formatTimeAgo(status.session_created_at) : 'Unknown'}
          </div>
        </div>

        {/* Last Check */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Last Check</div>
          <div className="text-xl font-semibold text-slate-900">
            {formatTimeAgo(status.last_check)}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Interval: {Math.round(check_interval_seconds / 60)}m
          </div>
        </div>

        {/* Last Refresh */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Last Refresh</div>
          <div className="text-xl font-semibold text-slate-900">
            {formatTimeAgo(status.last_refresh)}
          </div>
          {status.consecutive_failures > 0 && (
            <div className="text-xs text-amber-600 mt-1">
              {status.consecutive_failures} consecutive failure(s)
            </div>
          )}
        </div>
      </div>

      {/* 24h Statistics */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-action-500" />
          24h Statistics
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.total_checks}</div>
            <div className="text-xs text-slate-500">Total Checks</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{stats.successful_checks}</div>
            <div className="text-xs text-slate-500">Successful</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">{stats.failed_checks}</div>
            <div className="text-xs text-slate-500">Failed</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-amber-600">{stats.refreshes_triggered}</div>
            <div className="text-xs text-slate-500">Refreshes</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{stats.successful_refreshes}</div>
            <div className="text-xs text-slate-500">Refresh OK</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-600">{stats.failed_refreshes}</div>
            <div className="text-xs text-slate-500">Refresh Fail</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.uptime_percentage}%</div>
            <div className="text-xs text-slate-500">Uptime</div>
          </div>
        </div>
      </div>

      {/* 24h Timeline */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">24h Timeline</h2>
        <Timeline events={events} />
        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded" /> Success</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-400 rounded" /> Refresh</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded" /> Failure</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-200 rounded" /> No activity</span>
        </div>
      </div>

      {/* Event Log */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Event Log ({events.length} events)</h2>
        </div>
        <div className="overflow-auto max-h-96">
          {events.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No events recorded</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Time</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Type</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Details</th>
                </tr>
              </thead>
              <tbody>
                {[...events].reverse().map((event, idx) => (
                  <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-600 font-mono text-xs whitespace-nowrap">
                      {formatEventTime(event.iso_time)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        {getEventIcon(event.type)}
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getEventColor(event.type).replace('bg-', 'bg-').replace('500', '100')} ${getEventColor(event.type).replace('bg-', 'text-').replace('500', '700')}`}>
                          {event.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-slate-500 text-xs font-mono">
                      {Object.keys(event.details).length > 0 ? (
                        <span title={JSON.stringify(event.details, null, 2)}>
                          {Object.entries(event.details).map(([k, v]) => `${k}=${v}`).join(', ')}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Error message if present */}
      {status.error_message && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
          <h3 className="text-sm font-semibold text-red-800 mb-1">Last Error</h3>
          <p className="text-sm text-red-700 font-mono">{status.error_message}</p>
        </div>
      )}
    </div>
  )
}

export const lawyerDebugRoute = createRoute({
  getParentRoute: () => lawyerRoute,
  path: '/debug',
  component: DebugPage,
})
